import { FileDown, X } from "lucide-react";
import { hplcPath } from "@/lib/products";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoaModal({ product, onClose }) {
  if (!product) return null;
  return (
    <Dialog open={!!product} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="coa-modal-container">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Certificate of Analysis — Summary</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-px bg-slate-200 border border-slate-200 text-sm">
            {[["Compound", product.alias ? `${product.name} (${product.alias})` : product.name],
              ["Specification", product.specification],
              ["SKU", product.sku],
              ["Research Type", product.category],
              ...(product.coa_lot ? [["Batch / Lot", product.coa_lot]] : []),
              ...(product.coa_purity ? [["HPLC Purity", `${product.coa_purity}%`]] : []),
              ["COA Status", product.has_coa_file ? "Verified — lab PDF on file" : product.reference_coa ? `Reference COA — Lot ${product.reference_coa.lot} (${product.reference_coa.specification}); batch-matched COA ships with order` : "On file — independent HPLC/MS"],
              ["Storage", product.specification.includes("Liquid") ? "2–8°C" : "-20°C, lyophilized"],
              ["Availability", product.turnaround ? "5 business day turnaround" : `In stock${product.stock ? ` · ${product.stock} units` : ""}`],
              ["Use", "In-vitro laboratory research only"]].map(([k, v]) => (
              <div key={k} className="bg-white p-3">
                <p className="font-tech text-[10px] uppercase tracking-[0.18em] text-slate-500">{k}</p>
                <p className="font-tech text-sm font-medium text-slate-900 mt-1 break-words">{v}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#0B111A] border border-slate-800 p-4">
            <p className="font-tech text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">HPLC Chromatogram · 214nm</p>
            <svg viewBox="0 0 300 100" className="w-full h-24">
              <path d={hplcPath(product.id)} fill="none" stroke="#38bdf8" strokeWidth="1.5" />
              <line x1="0" y1="90" x2="300" y2="90" stroke="#1e293b" strokeWidth="1" />
            </svg>
            <div className="flex justify-between font-tech text-[10px] text-slate-500 mt-1">
              <span>RT 0 min</span><span>{product.coa_purity ? `Purity ${product.coa_purity}%` : "Purity verified per batch COA"}</span><span>15 min</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            The full batch-matched Certificate of Analysis from our supplier's independent analytical
            laboratory ships with every order. Research Use Only.
          </p>
          {product.has_coa_file ? (
            <a href={`${API}/coa/${product.id}`} target="_blank" rel="noopener noreferrer" data-testid={`coa-download-${product.id}`}
              className="flex items-center justify-center gap-2 w-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium py-2.5 rounded-full transition-colors">
              <FileDown className="h-4 w-4" /> Download Full COA (PDF)
            </a>
          ) : product.reference_coa ? (
            <a href={`${API}/coa/${product.reference_coa.product_id}`} target="_blank" rel="noopener noreferrer" data-testid={`coa-reference-download-${product.id}`}
              className="flex items-center justify-center gap-2 w-full border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white text-sm font-medium py-2.5 rounded-full transition-colors">
              <FileDown className="h-4 w-4" /> Download Reference COA ({product.reference_coa.specification.split(" ")[0]} lot)
            </a>
          ) : null}
          <Button variant="outline" onClick={onClose} data-testid="coa-modal-close-btn" className="w-full">
            <X className="h-4 w-4 mr-2" /> Close Dossier
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
